import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
  Modal,
  Alert,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import {
  FaLink,
  FaFileExcel,
  FaSync,
  FaCheckCircle,
  FaUserPlus,
  FaExchangeAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import homologacionComensalesLogiflexApi from "../../../services/api/homologacionComensalesLogiflexApi";
import clienteApi from "../../../services/api/clienteApi";
import "react-toastify/dist/ReactToastify.css";

const ESTADOS = [
  { value: "", label: "Todos" },
  { value: "HOMOLOGADO", label: "Homologados" },
  { value: "CONFLICTO_NOMBRE", label: "Conflicto nombre" },
  { value: "CONFLICTO_DNI", label: "Conflicto DNI" },
  { value: "CREAR", label: "Por crear (solo Logiflex)" },
  { value: "SIN_MATCH_SGC", label: "Solo en SGC" },
  { value: "ERROR", label: "Errores" },
];

const badgeVariant = (estado) => {
  switch (estado) {
    case "HOMOLOGADO":
      return "success";
    case "CREAR":
      return "info";
    case "CONFLICTO_NOMBRE":
    case "CONFLICTO_DNI":
      return "danger";
    case "SIN_MATCH_SGC":
      return "warning";
    case "ERROR":
      return "dark";
    default:
      return "secondary";
  }
};

const HomologacionComensalesLogiflex = () => {
  const selectedProject = JSON.parse(
    localStorage.getItem("selectedProject") || "null"
  );

  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [clientes, setClientes] = useState([]);
  const [idCliente, setIdCliente] = useState("");
  const [accionKey, setAccionKey] = useState(null);
  const [modalConfirm, setModalConfirm] = useState({ type: null, fila: null });

  const clienteSeleccionadoNombre = useMemo(() => {
    if (!idCliente) return null;
    const c = clientes.find((x) => String(x.id) === String(idCliente));
    return c?.nombre || null;
  }, [clientes, idCliente]);

  useEffect(() => {
    const loadClientes = async () => {
      if (!selectedProject?.id) return;
      try {
        const response = await clienteApi.getByProyecto(selectedProject.id);
        const data = response.data?.data || response.data || [];
        const list = Array.isArray(data) ? data : [];
        setClientes(list);
        if (list.length === 1) {
          setIdCliente(String(list[0].id));
        }
      } catch (err) {
        console.error(err);
        toast.error("No se pudieron cargar clientes del proyecto");
      }
    };
    loadClientes();
  }, [selectedProject?.id]);

  const filasFiltradas = useMemo(() => {
    const filas = resultado?.filas || [];
    return filas.filter((f) => {
      if (filtroEstado && f.estado !== filtroEstado) return false;
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      const texto = [
        f.dni,
        f.nombre_sgc,
        f.nombre_logiflex,
        f.empresa_logiflex,
        f.motivo,
        f.comensal_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(q);
    });
  }, [resultado, filtroEstado, busqueda]);

  const ejecutar = async (aplicar) => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }

    if (aplicar) {
      const ok = window.confirm(
        "Se marcarán homologados, se guardará el nombre Logiflex y se crearán " +
          "los comensales faltantes con acceso a todos los comedores del proyecto. ¿Continuar?"
      );
      if (!ok) return;
      setAplicando(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await homologacionComensalesLogiflexApi.ejecutar(
        selectedProject.id,
        {
          aplicar,
          id_cliente: idCliente ? Number(idCliente) : null,
        }
      );
      setResultado(response.data);
      toast.success(response.data?.message || "Proceso completado");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error en homologación"
      );
    } finally {
      setLoading(false);
      setAplicando(false);
    }
  };

  const actualizarFilaLocal = (matchFn, patch) => {
    setResultado((prev) => {
      if (!prev?.filas) return prev;
      const filas = prev.filas.map((f) => (matchFn(f) ? { ...f, ...patch } : f));
      const resumen = {
        ...(prev.resumen || {}),
        homologados: filas.filter((f) => f.estado === "HOMOLOGADO").length,
        conflicto_nombre: filas.filter((f) => f.estado === "CONFLICTO_NOMBRE").length,
        crear: filas.filter((f) => f.estado === "CREAR").length,
      };
      return { ...prev, filas, resumen };
    });
  };

  const cerrarModalConfirm = () => {
    if (accionKey) return;
    setModalConfirm({ type: null, fila: null });
  };

  const abrirModalConflicto = (fila) => {
    if (!selectedProject?.id || !fila?.comensal_id || !fila?.nombre_logiflex) {
      toast.error("Datos incompletos para actualizar el nombre");
      return;
    }
    setModalConfirm({ type: "conflicto", fila });
  };

  const abrirModalCrear = (fila) => {
    if (!selectedProject?.id || !fila?.dni || !fila?.nombre_logiflex) {
      toast.error("Datos incompletos para crear el comensal");
      return;
    }
    setModalConfirm({ type: "crear", fila });
  };

  const confirmarResolverConflictoNombre = async () => {
    const fila = modalConfirm.fila;
    if (!fila || !selectedProject?.id) return;

    const key = `conflicto-${fila.comensal_id}`;
    setAccionKey(key);
    try {
      const response = await homologacionComensalesLogiflexApi.resolverConflictoNombre(
        selectedProject.id,
        {
          comensal_id: fila.comensal_id,
          nombre_logiflex: fila.nombre_logiflex,
          empresa_logiflex: fila.empresa_logiflex || null,
        }
      );
      const data = response.data?.data || {};
      actualizarFilaLocal(
        (f) => f.comensal_id === fila.comensal_id && f.estado === "CONFLICTO_NOMBRE",
        {
          estado: "HOMOLOGADO",
          nombre_sgc: data.nombre_sgc || fila.nombre_logiflex,
          nombre_proveedor_actual: data.nombre_sgc || fila.nombre_logiflex,
          homologado_actual: true,
          motivo: "Nombre SGC actualizado manualmente con Logiflex",
        }
      );
      toast.success(response.data?.message || "Nombre actualizado");
      setModalConfirm({ type: null, fila: null });
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error al actualizar nombre"
      );
    } finally {
      setAccionKey(null);
    }
  };

  const confirmarCrearComensalManual = async () => {
    const fila = modalConfirm.fila;
    if (!fila || !selectedProject?.id) return;

    const key = `crear-${fila.dni}`;
    setAccionKey(key);
    try {
      const response = await homologacionComensalesLogiflexApi.crearComensal(
        selectedProject.id,
        {
          dni: fila.dni,
          nombre_logiflex: fila.nombre_logiflex,
          empresa_logiflex: fila.empresa_logiflex || null,
          cargo_logiflex: fila.cargo_logiflex || null,
          id_cliente: idCliente ? Number(idCliente) : null,
        }
      );
      const data = response.data?.data || {};
      actualizarFilaLocal(
        (f) => f.dni === fila.dni && f.estado === "CREAR",
        {
          estado: "HOMOLOGADO",
          comensal_id: data.comensal_id,
          nombre_sgc: data.nombre_sgc || fila.nombre_logiflex,
          homologado_actual: true,
          motivo: response.data?.message || "Comensal creado manualmente",
        }
      );
      toast.success(response.data?.message || "Comensal creado");
      setModalConfirm({ type: null, fila: null });
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error al crear comensal"
      );
    } finally {
      setAccionKey(null);
    }
  };

  const exportarExcel = () => {
    if (!filasFiltradas.length) {
      toast.warning("No hay filas para exportar");
      return;
    }

    const rows = filasFiltradas.map((f) => ({
      Estado: f.estado,
      DNI: f.dni ?? "",
      "ID Comensal": f.comensal_id ?? "",
      "Nombre SGC": f.nombre_sgc ?? "",
      "Nombre Logiflex": f.nombre_logiflex ?? "",
      "Empresa Logiflex": f.empresa_logiflex ?? "",
      Homologado: f.homologado_actual ? "Sí" : "No",
      Motivo: f.motivo ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Homologación");

    if (resultado?.resumen) {
      const wsRes = XLSX.utils.json_to_sheet([resultado.resumen]);
      XLSX.utils.book_append_sheet(wb, wsRes, "Resumen");
    }

    const fecha = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `homologacion_comensales_logiflex_${fecha}.xlsx`);
    toast.success("Excel exportado");
  };

  const resumen = resultado?.resumen;

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding-bottom: 2rem;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; opacity: 0.95; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; opacity: 0.95; }
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-info:hover { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control { border-radius: 10px; }
          .dh-alert-info {
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            border: 1px solid #93c5fd;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            color: #1e40af;
          }
          .stat-box {
            text-align: center;
            padding: 1rem;
            border-radius: 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
          }
          .stat-box h4 { margin: 0; font-weight: 700; }
          .stat-box small { color: #64748b; }
          .table-homologacion { font-size: 0.875rem; }
          .table-homologacion th {
            background: #f1f5f9;
            position: sticky;
            top: 0;
            z-index: 1;
          }
        `}
      </style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaLink className="me-2" />
            Homologación Comensales — Logiflex
          </h1>
          <p className="dh-main-subtitle">
            Cruza empleados activos de Logiflex con comensales SGC por DNI y
            valida nombres
          </p>
        </Container>
      </div>

      <Container>
        <Row className="mb-4">
          <Col>
            <div className="dh-alert dh-alert-info">
              1) Match por DNI · 2) Validación de nombre/apellidos · 3) Empleados
              solo en Logiflex se crean como comensales (origen Local) con acceso
              a todos los comedores del proyecto. Use primero <strong>Vista previa</strong>.
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <Card className="dh-card">
              <Card.Header className="dh-card-header dh-card-header-primary">
                Ejecutar homologación
              </Card.Header>
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col md={5}>
                    <Form.Label className="dh-form-label">
                      Cliente por defecto (altas automáticas)
                    </Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={idCliente}
                      onChange={(e) => setIdCliente(e.target.value)}
                    >
                      <option value="">— Resolver por empresa Logiflex —</option>
                      {clientes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={7} className="d-flex flex-wrap gap-2">
                    <Button
                      className="btn-dh-info"
                      onClick={() => ejecutar(false)}
                      disabled={loading || aplicando}
                    >
                      {loading ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaSync className="me-2" />
                      )}
                      Vista previa
                    </Button>
                    <Button
                      className="btn-dh-primary"
                      onClick={() => ejecutar(true)}
                      disabled={loading || aplicando}
                    >
                      {aplicando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaCheckCircle className="me-2" />
                      )}
                      Ejecutar y aplicar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {resumen && (
          <Row className="mb-4 g-3">
            {[
              ["Logiflex", resumen.total_logiflex],
              ["SGC", resumen.total_sgc],
              ["Homologados", resumen.homologados],
              ["Conf. nombre", resumen.conflicto_nombre],
              ["Por crear", resumen.crear],
              ["Creados", resumen.creados],
              ["Solo SGC", resumen.sin_match_sgc],
            ].map(([label, val]) => (
              <Col key={label} xs={6} md={3} lg>
                <div className="stat-box">
                  <h4>{val ?? 0}</h4>
                  <small>{label}</small>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {resultado && (
          <Row>
            <Col>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-success d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Resultado ({filasFiltradas.length} filas)</span>
                  <Button
                    size="sm"
                    className="btn-dh-secondary"
                    onClick={exportarExcel}
                    disabled={!filasFiltradas.length}
                  >
                    <FaFileExcel className="me-1" /> Exportar Excel
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <Form.Label className="dh-form-label">
                        Filtrar por estado
                      </Form.Label>
                      <Form.Select
                        className="dh-form-control"
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                      >
                        {ESTADOS.map((e) => (
                          <option key={e.value || "all"} value={e.value}>
                            {e.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={8}>
                      <Form.Label className="dh-form-label">Buscar</Form.Label>
                      <Form.Control
                        className="dh-form-control"
                        placeholder="DNI, nombre, empresa..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <div style={{ maxHeight: "520px", overflow: "auto" }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      className="table-homologacion mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Estado</th>
                          <th>DNI</th>
                          <th>ID SGC</th>
                          <th>Nombre SGC</th>
                          <th>Nombre Logiflex</th>
                          <th>Empresa</th>
                          <th>Homologado</th>
                          <th>Motivo</th>
                          <th style={{ width: "140px" }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filasFiltradas.length === 0 ? (
                          <tr>
                            <td
                              colSpan={9}
                              className="text-center text-muted py-4"
                            >
                              Sin registros para el filtro seleccionado
                            </td>
                          </tr>
                        ) : (
                          filasFiltradas.map((f, idx) => {
                            const keyConflicto = `conflicto-${f.comensal_id}`;
                            const keyCrear = `crear-${f.dni}`;
                            const busy =
                              accionKey === keyConflicto || accionKey === keyCrear;
                            return (
                            <tr key={`${f.estado}-${f.dni || ""}-${f.comensal_id || idx}`}>
                              <td>
                                <Badge bg={badgeVariant(f.estado)}>
                                  {f.estado}
                                </Badge>
                              </td>
                              <td>{f.dni || "—"}</td>
                              <td>{f.comensal_id ?? "—"}</td>
                              <td>{f.nombre_sgc || "—"}</td>
                              <td>{f.nombre_logiflex || "—"}</td>
                              <td>
                                <small>{f.empresa_logiflex || "—"}</small>
                              </td>
                              <td>{f.homologado_actual ? "Sí" : "No"}</td>
                              <td>
                                <small className="text-muted">
                                  {f.motivo || ""}
                                </small>
                              </td>
                              <td>
                                {f.estado === "CONFLICTO_NOMBRE" && (
                                  <Button
                                    size="sm"
                                    className="btn-dh-primary"
                                    disabled={busy || !!accionKey}
                                    onClick={() => abrirModalConflicto(f)}
                                    title="Usar nombre Logiflex en SGC"
                                  >
                                    {busy ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <>
                                        <FaExchangeAlt className="me-1" />
                                        Usar nombre LF
                                      </>
                                    )}
                                  </Button>
                                )}
                                {f.estado === "CREAR" && (
                                  <Button
                                    size="sm"
                                    className="btn-dh-info"
                                    disabled={busy || !!accionKey}
                                    onClick={() => abrirModalCrear(f)}
                                    title="Crear comensal en SGC"
                                  >
                                    {busy ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <>
                                        <FaUserPlus className="me-1" />
                                        Crear
                                      </>
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      <Modal
        show={modalConfirm.type === "conflicto"}
        onHide={cerrarModalConfirm}
        centered
        backdrop="static"
        contentClassName="border-0 shadow rounded-3 overflow-hidden"
      >
        <Modal.Header
          closeButton={!accionKey}
          className="text-white border-0"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaExchangeAlt />
            Confirmar cambio de nombre
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 pb-2">
          <Alert variant="warning" className="d-flex gap-2 align-items-start mb-3">
            <FaExclamationTriangle className="mt-1 flex-shrink-0" />
            <div className="small mb-0">
              Se reemplazará el <strong>nombre SGC</strong> por el de Logiflex y el
              comensal quedará marcado como <strong>homologado</strong>.
            </div>
          </Alert>
          <Row className="g-3">
            <Col xs={12}>
              <div className="p-3 rounded-3 border bg-light">
                <small className="text-muted d-block mb-1">Comensal SGC</small>
                <div className="fw-semibold">
                  #{modalConfirm.fila?.comensal_id} · DNI {modalConfirm.fila?.dni || "—"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-3 border h-100">
                <small className="text-muted d-block mb-1">Nombre actual (SGC)</small>
                <div className="fw-semibold text-danger">
                  {modalConfirm.fila?.nombre_sgc || "—"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-3 border h-100 border-success">
                <small className="text-muted d-block mb-1">Nuevo nombre (Logiflex)</small>
                <div className="fw-semibold text-success">
                  {modalConfirm.fila?.nombre_logiflex || "—"}
                </div>
              </div>
            </Col>
            {modalConfirm.fila?.empresa_logiflex && (
              <Col xs={12}>
                <div className="p-3 rounded-3 border">
                  <small className="text-muted d-block mb-1">Empresa Logiflex</small>
                  <div className="fw-semibold">
                    {modalConfirm.fila.empresa_logiflex}
                  </div>
                  <small className="text-muted">
                    También se actualizará el campo empresa del comensal.
                  </small>
                </div>
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-3 gap-2">
          <Button
            className="btn-dh-secondary"
            onClick={cerrarModalConfirm}
            disabled={!!accionKey}
          >
            Cancelar
          </Button>
          <Button
            className="btn-dh-primary"
            onClick={confirmarResolverConflictoNombre}
            disabled={!!accionKey}
          >
            {accionKey ? (
              <>
                <Spinner size="sm" className="me-2" />
                Actualizando...
              </>
            ) : (
              <>
                <FaExchangeAlt className="me-2" />
                Confirmar cambio
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalConfirm.type === "crear"}
        onHide={cerrarModalConfirm}
        centered
        backdrop="static"
        contentClassName="border-0 shadow rounded-3 overflow-hidden"
      >
        <Modal.Header
          closeButton={!accionKey}
          className="text-white border-0"
          style={{
            background: "linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)",
          }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaUserPlus />
            Confirmar alta de comensal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 pb-2">
          <Alert variant="info" className="dh-alert dh-alert-info mb-3">
            Se creará un comensal nuevo con origen <strong>Local</strong> y acceso a{" "}
            <strong>todos los comedores activos</strong> del proyecto.
          </Alert>
          <Row className="g-3">
            <Col md={6}>
              <div className="p-3 rounded-3 border bg-light h-100">
                <small className="text-muted d-block mb-1">DNI</small>
                <div className="fw-semibold fs-5">{modalConfirm.fila?.dni || "—"}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-3 border h-100">
                <small className="text-muted d-block mb-1">Tipo origen</small>
                <div className="fw-semibold">Local</div>
              </div>
            </Col>
            <Col xs={12}>
              <div className="p-3 rounded-3 border border-primary">
                <small className="text-muted d-block mb-1">Nombre (Logiflex)</small>
                <div className="fw-semibold">
                  {modalConfirm.fila?.nombre_logiflex || "—"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-3 border h-100">
                <small className="text-muted d-block mb-1">Empresa</small>
                <div className="fw-semibold">
                  {modalConfirm.fila?.empresa_logiflex || "—"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-3 border h-100">
                <small className="text-muted d-block mb-1">Cargo</small>
                <div className="fw-semibold">
                  {modalConfirm.fila?.cargo_logiflex || "—"}
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <div className="p-3 rounded-3 border">
                <small className="text-muted d-block mb-1">Cliente</small>
                <div className="fw-semibold">
                  {clienteSeleccionadoNombre ||
                    "Se resolverá por empresa Logiflex (o único cliente del proyecto)"}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-3 gap-2">
          <Button
            className="btn-dh-secondary"
            onClick={cerrarModalConfirm}
            disabled={!!accionKey}
          >
            Cancelar
          </Button>
          <Button
            className="btn-dh-primary"
            onClick={confirmarCrearComensalManual}
            disabled={!!accionKey}
          >
            {accionKey ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <FaUserPlus className="me-2" />
                Confirmar creación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default HomologacionComensalesLogiflex;
